import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Toaster } from "react-hot-toast";
import { notifyError, notifySuccess } from "../../../../components/AppToast";
import { useAuth } from "../../../../app/providers/AuthProvider";
import { PLATFORM_ADMIN, STAFF_ROLE_LABELS } from "../../permissions";
import { useStaffPermissions } from "../../hooks";
import { staffApiError } from "../apiErrors";
import { useReactivateStaff, useStaffList } from "../hooks";
import { ConfirmDeactivateModal } from "../components/ConfirmDeactivateModal";
import { EditStaffRolesModal } from "../components/EditStaffRolesModal";
import { InviteStaffModal } from "../components/InviteStaffModal";

const COLUMNS = [
  {
    id: "member",
    header: "Miembro",
    accessorFn: (row) => `${row.email} ${row.name}`,
    cell: ({ row }) => (
      <div>
        <div>{row.original.email}</div>
        <div className="text-muted small">{row.original.name}</div>
      </div>
    ),
  },
  {
    id: "roles",
    header: "Roles",
    accessorFn: (row) => (row.staff_roles ?? []).join(" "),
    cell: ({ row }) => (
      <div className="d-flex gap-1 flex-wrap">
        {(row.original.staff_roles ?? []).map((role) => (
          <Badge bg="secondary" key={role}>
            {STAFF_ROLE_LABELS[role] ?? role}
          </Badge>
        ))}
      </div>
    ),
  },
  {
    id: "status",
    header: "Estado",
    accessorFn: (row) => (row.is_active ? "Activo" : "Inactivo"),
    cell: ({ row }) => (
      <Badge bg={row.original.is_active ? "success" : "secondary"}>
        {row.original.is_active ? "Activo" : "Inactivo"}
      </Badge>
    ),
  },
  {
    accessorKey: "created_at",
    header: "Alta",
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? new Date(v).toLocaleDateString("es-CO") : "—";
    },
  },
  { id: "actions", header: "Acciones" },
];

// ── Acciones por fila ─────────────────────────────────────────────────────────
// Extraídas porque se renderizan dos veces (tabla de escritorio + tarjeta
// móvil): así los gates y el caso "soy yo" se escriben una sola vez.

function StaffRowActions({
  member,
  isSelf,
  isLastAdmin,
  onEdit,
  onDeactivate,
  onReactivate,
  busy,
}) {
  // Las dos reglas anti-bloqueo del backend (services.py::assert_no_lockout) no
  // admiten excepción, así que se reflejan deshabilitando el control en vez de
  // dejar que el usuario descubra el 409 al pulsar.
  const blockedReason = (() => {
    if (isSelf) return "No puedes desactivar tu propia cuenta";
    if (isLastAdmin)
      return "Es el único administrador de plataforma activo; asigna el rol a otra persona antes de desactivarlo";
    return null;
  })();

  return (
    <div className="d-flex gap-2 flex-wrap">
      <Button
        size="sm"
        variant="outline-primary"
        style={{ minHeight: 44 }}
        onClick={() => onEdit(member)}
        aria-label={`Editar roles de ${member.email}`}
      >
        Editar roles
      </Button>
      {member.is_active ? (
        // El title necesita un envoltorio: un <button disabled> no dispara
        // eventos de ratón, así que el navegador no mostraría el tooltip.
        <span title={blockedReason ?? undefined} className="d-inline-block">
          <Button
            size="sm"
            variant="outline-danger"
            style={{ minHeight: 44 }}
            onClick={() => onDeactivate(member)}
            disabled={Boolean(blockedReason)}
            aria-label={`Desactivar a ${member.email}`}
          >
            Desactivar
          </Button>
        </span>
      ) : (
        <Button
          size="sm"
          variant="outline-success"
          style={{ minHeight: 44 }}
          onClick={() => onReactivate(member)}
          disabled={busy}
          aria-label={`Reactivar a ${member.email}`}
        >
          Reactivar
        </Button>
      )}
    </div>
  );
}

// ── StaffPage ─────────────────────────────────────────────────────────────────

function StaffPage() {
  const { data: staff, isLoading, isError, error } = useStaffList();
  const { user } = useAuth();
  // La ruta ya restringe a PLATFORM_ADMIN (requiredRoles); esto mantiene la
  // página honesta si la matriz cambia. Gating de UX — el backend revalida.
  const { can } = useStaffPermissions();
  const canInvite = can("staff", "create");
  const canManage = can("staff", "update") || can("staff", "delete");

  const reactivate = useReactivateStaff();
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const columns = useMemo(
    () => (canManage ? COLUMNS : COLUMNS.filter((c) => c.id !== "actions")),
    [canManage]
  );

  // El endpoint no pagina ni filtra por texto: se hace en cliente.
  const filtered = useMemo(() => {
    return (staff ?? []).filter((m) => {
      if (statusFilter === "active") return m.is_active;
      if (statusFilter === "inactive") return !m.is_active;
      return true;
    });
  }, [staff, statusFilter]);

  const table = useReactTable({
    data: filtered,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
  });

  // `/users/me/` no devuelve `id` (solo username/email/…), así que la identidad
  // se compara por correo — único en el backend, que valida la invitación con
  // `iexact`. Compararlo por `id` no funcionaba: era `undefined === 140`.
  const myEmail = user?.email?.toLowerCase() ?? null;

  // Si solo queda UN administrador de plataforma activo, ni se le puede
  // desactivar ni quitarle el rol (assert_no_lockout). Se calcula sobre la lista
  // completa, no sobre `filtered`: un filtro no puede cambiar quién es el último.
  const lastActiveAdminEmail = useMemo(() => {
    const admins = (staff ?? []).filter(
      (m) => m.is_active && (m.staff_roles ?? []).includes(PLATFORM_ADMIN)
    );
    return admins.length === 1 ? admins[0].email?.toLowerCase() : null;
  }, [staff]);

  const rowFlags = (member) => {
    const email = member.email?.toLowerCase();
    return {
      isSelf: Boolean(myEmail) && email === myEmail,
      isLastAdmin: Boolean(lastActiveAdminEmail) && email === lastActiveAdminEmail,
    };
  };

  function handleReactivate(member) {
    reactivate.mutate(member.id, {
      onSuccess: () => notifySuccess(`${member.email} quedó activo de nuevo.`),
      onError: (err) =>
        notifyError(staffApiError(err, "No se pudo reactivar la cuenta.")),
    });
  }

  const rowActionProps = {
    onEdit: setEditing,
    onDeactivate: setDeactivating,
    onReactivate: handleReactivate,
    busy: reactivate.isPending,
  };

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner aria-label="Cargando staff" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {staffApiError(error, "No se pudo cargar la lista de staff.")}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h4 className="mb-0">Staff y roles</h4>
        {canInvite && (
          <Button
            variant="primary"
            size="sm"
            style={{ minHeight: 44 }}
            onClick={() => setShowInvite(true)}
            aria-label="Invitar nuevo miembro de staff"
          >
            + Invitar miembro
          </Button>
        )}
      </div>

      <Row className="g-2 mb-3">
        <Col xs={12} sm={5}>
          <Form.Control
            placeholder="Buscar por correo, nombre o rol..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Buscar miembro de staff"
            style={{ minHeight: 44 }}
          />
        </Col>
        <Col xs={12} sm={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtrar por estado"
            style={{ minHeight: 44 }}
          >
            <option value="">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </Form.Select>
        </Col>
      </Row>

      {filtered.length === 0 && (
        <Alert variant="light" className="border">
          No hay miembros de staff que coincidan con el filtro.
        </Alert>
      )}

      {/* ── Escritorio: tabla ── */}
      <div className="d-none d-sm-block">
        <div className="table-responsive">
          <Table hover bordered size="sm" className="align-middle">
            <thead className="table-light">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    if (cell.column.id === "actions") {
                      return (
                        <td key={cell.id}>
                          <StaffRowActions
                            member={row.original}
                            {...rowFlags(row.original)}
                            {...rowActionProps}
                          />
                        </td>
                      );
                    }
                    return (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="Página anterior"
          >
            ‹ Anterior
          </Button>
          <span className="small text-muted">
            Página {table.getState().pagination.pageIndex + 1} de{" "}
            {Math.max(1, table.getPageCount())}
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Página siguiente"
          >
            Siguiente ›
          </Button>
        </div>
      </div>

      {/* ── Móvil: tarjetas (respetan filtro y paginación) ── */}
      <div className="d-sm-none">
        {table.getRowModel().rows.map((row) => {
          const m = row.original;
          return (
            <Card key={m.id} className="mb-2">
              <Card.Body className="py-2 px-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <strong>{m.email}</strong>
                    <div className="text-muted small">{m.name}</div>
                  </div>
                  <Badge bg={m.is_active ? "success" : "secondary"}>
                    {m.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
                <div className="d-flex gap-1 flex-wrap mt-2">
                  {(m.staff_roles ?? []).map((role) => (
                    <Badge bg="secondary" key={role}>
                      {STAFF_ROLE_LABELS[role] ?? role}
                    </Badge>
                  ))}
                </div>
                {canManage && (
                  <div className="mt-2">
                    <StaffRowActions
                      member={m}
                      {...rowFlags(m)}
                      {...rowActionProps}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {canInvite && (
        <InviteStaffModal show={showInvite} onHide={() => setShowInvite(false)} />
      )}
      {editing && (
        <EditStaffRolesModal
          member={editing}
          isLastAdmin={rowFlags(editing).isLastAdmin}
          onHide={() => setEditing(null)}
        />
      )}
      {deactivating && (
        <ConfirmDeactivateModal
          member={deactivating}
          onHide={() => setDeactivating(null)}
        />
      )}
      <Toaster position="top-right" />
    </Container>
  );
}

export { StaffPage };
